import type {
  CapabilityFinding,
  DumpCapacityReport,
  ExecutableExitReport,
  ExitLadderRow,
  IdentityReport,
  RugCapacityReport,
} from "./shared/report";

export type EvidenceSectionState = "idle" | "loading" | "ready" | "error";
export type SimpleAssessmentTone =
  | "danger"
  | "warning"
  | "calm"
  | "unknown"
  | "not_assessed"
  | "checking";

export interface SimpleCoreCheck {
  number: "01" | "02" | "03" | "04";
  title: string;
  question: string;
  result: string;
  tone: SimpleAssessmentTone;
}

export interface SimpleAssessment {
  label:
    | "AVOID FOR NOW"
    | "HIGH CAUTION"
    | "MEDIUM CAUTION"
    | "NO MAJOR RED FLAGS FOUND"
    | "COULDN'T VERIFY"
    | "CHECKING…";
  headline: string;
  summary: string;
  tone: SimpleAssessmentTone;
  checks: [SimpleCoreCheck, SimpleCoreCheck, SimpleCoreCheck, SimpleCoreCheck];
  reasons: string[];
  caveat: string;
}

interface BuildSimpleAssessmentInput {
  identityReport: IdentityReport | null;
  rugCapacity: RugCapacityReport | null;
  dumpCapacity: DumpCapacityReport | null;
  executableExit: ExecutableExitReport | null;
  identityState: EvidenceSectionState;
  capabilityState: EvidenceSectionState;
  dumpState: EvidenceSectionState;
  exitState: EvidenceSectionState;
}

const severeImpact = new Set(["near_total", "arbitrary_rules"]);
const failedExitStates = new Set([
  "poor_proceeds",
  "no_supported_route",
  "simulation_failed",
  "insufficient_liquidity",
]);
const unknownExitStates = new Set([
  "holder_context_unavailable",
  "provider_unavailable",
  "provider_rate_limited",
  "unsupported",
  "unknown",
]);

export function buildSimpleAssessment({
  identityReport,
  rugCapacity,
  dumpCapacity,
  executableExit,
  identityState,
  capabilityState,
  dumpState,
  exitState,
}: BuildSimpleAssessmentInput): SimpleAssessment {
  const findings = rugCapacity?.findings ?? [];
  const observed = findings.filter((finding) => finding.status === "observed");
  const severeFindings = observed.filter((finding) => severeImpact.has(finding.impactClass));
  const decisiveSevere = severeFindings.filter((finding) =>
    ["wallet_shaped", "token_multisig", "fixture"].includes(finding.control.kind),
  );
  const unknownFindings = findings.filter((finding) => finding.status === "unknown");
  const notAssessedFindings = findings.filter(
    (finding) => finding.status === "not_assessed",
  );
  const controllerGaps = [...unknownFindings, ...notAssessedFindings];
  const rows = executableExit?.rows ?? [];
  const failedRows = rows.filter(
    (row) =>
      failedExitStates.has(row.state) ||
      (row.estimatedRecoveryPercentage !== null && row.estimatedRecoveryPercentage < 80),
  );
  const unknownRows = rows.filter(
    (row) =>
      !failedRows.includes(row) &&
      (unknownExitStates.has(row.state) || row.expectedOutputAssetDelta === null),
  );

  const checks: [SimpleCoreCheck, SimpleCoreCheck, SimpleCoreCheck, SimpleCoreCheck] = [
    buildIdentityCheck(identityReport, identityState),
    buildControllerCheck(
      findings,
      observed,
      unknownFindings,
      notAssessedFindings,
      capabilityState,
    ),
    buildDumpCheck(dumpCapacity, dumpState),
    buildExitCheck(rows, failedRows, unknownRows, exitState),
  ];

  const caveat =
    "This is a risk posture from the evidence checked—not proof of fraud, a promise of safety, or financial advice.";

  if (decisiveSevere.length || failedRows.length) {
    return {
      label: "AVOID FOR NOW",
      headline: "This setup could fuck off with your money. Stay away for now.",
      summary: "A directly controlled power or failed exit could cause severe loss. The result names the evidence below; it does not guess anyone’s intent.",
      tone: "danger",
      checks,
      reasons: pickReasons([
        decisiveSevere[0]?.summary ?? null,
        ...rankExitFailures(failedRows).map(exitFailureReason),
        dumpCapacity?.riskBand === "high" ? dumpCapacity.headline : null,
        ...decisiveSevere.slice(1).map((finding) => finding.summary),
      ]),
      caveat,
    };
  }

  const states = [identityState, capabilityState, dumpState, exitState];
  const allComplete = states.every((state) => state === "ready");
  const identityComplete = Boolean(identityReport && identityReport.status !== "unknown");
  const controllerComplete = Boolean(rugCapacity && findings.length && !controllerGaps.length);
  const exitsComplete = Boolean(executableExit && rows.length && !unknownRows.length);
  const highHolderConcentration = dumpCapacity?.riskBand === "high";
  const mediumHolderConcentration = dumpCapacity?.riskBand === "elevated";
  if (observed.length || highHolderConcentration) {
    const programContext = severeFindings.some((finding) =>
      ["program_controlled", "unresolved"].includes(finding.control.kind),
    );
    return {
      label: "HIGH CAUTION",
      headline: highHolderConcentration && !observed.length
        ? "Cute coin. Big wallet. Proceed with side-eye."
        : programContext
          ? "Protocol-owned can be normal. The protocol constraints still need a proper look."
          : "Cute coin. Sharp teeth. Slow down.",
      summary: programContext
        ? "Program-controlled minting can be expected for a receipt token, but program admins, upgrades, backing, and redemption still matter."
        : highHolderConcentration
          ? "Working exits now do not remove the risk that a large holder sells into limited demand."
          : "Working sell quotes do not cancel the ability to change supply, holdings, transfers, fees, or liquidity.",
      tone: "warning",
      checks,
      reasons: pickReasons([
        ...observed.map((finding) => finding.summary),
        highHolderConcentration ? dumpCapacity?.headline ?? null : null,
        identityReport?.status === "unverified"
          ? "Neither checked identity registry confirmed the exact mint."
          : identityReport?.status === "unknown"
            ? "One or more identity registry checks did not complete."
            : null,
        controllerGaps.length
          ? controllerGapReason(controllerGaps)
          : null,
      ]),
      caveat,
    };
  }

  if (
    mediumHolderConcentration &&
    allComplete &&
    identityComplete &&
    controllerComplete &&
    exitsComplete
  ) {
    return {
      label: "MEDIUM CAUTION",
      headline: "Cute coin. Big wallet. Proceed with side-eye.",
      summary: "Working exits now do not remove the risk that a large holder sells into limited demand.",
      tone: "warning",
      checks,
      reasons: pickReasons([
        dumpCapacity?.headline ?? null,
        identityReport?.status === "unverified"
          ? "Neither checked identity registry confirmed the exact mint."
          : null,
      ]),
      caveat,
    };
  }

  const dumpComplete = dumpCapacity?.riskBand === "low_observed";
  if (allComplete && identityComplete && controllerComplete && dumpComplete && exitsComplete) {
    const lowestRecovery = lowestComparableRecovery(rows);
    return {
      label: "NO MAJOR RED FLAGS FOUND",
      headline: "Looking decent, bestie. Still not a crystal ball.",
      summary: "Nothing ugly showed up in these four checks: no covered dangerous control, no large wallet-shaped concentration, and every tested exit returned proceeds.",
      tone: "calm",
      checks,
      reasons: pickReasons([
        identityReport?.status === "registry_recognized"
          ? `${identityReport.sources.length} identity ${identityReport.sources.length === 1 ? "registry" : "registries"} recognized this exact mint.`
          : identityReport?.status === "unknown"
            ? "Identity evidence remains incomplete because a registry check failed."
            : "Neither checked registry confirmed this exact mint.",
        "No dangerous controller power was observed in the areas checked.",
        lowestRecovery === null
          ? `All ${rows.length} tested exit sizes returned proceeds.`
          : `All ${rows.length} exits returned proceeds; the weakest quoted ${lowestRecovery.toFixed(1)}%.`,
      ]),
      caveat,
    };
  }

  const isStillChecking = states.some((state) => state === "loading" || state === "idle");
  if (isStillChecking && states.every((state) => state !== "error")) {
    return {
      label: "CHECKING…",
      headline: "Peeking under the rug…",
      summary: "Identity, controller power, holder concentration, and exits arrive as separate receipts.",
      tone: "checking",
      checks,
      reasons: ["No reassuring result is shown until every practical check has enough evidence."],
      caveat,
    };
  }

  const allControllerRowsUnknown = Boolean(findings.length && unknownFindings.length === findings.length);
  const allExitRowsUnknown = Boolean(rows.length && unknownRows.length === rows.length);
  const broadFailure =
    identityState === "error" ||
    capabilityState === "error" ||
    dumpState === "error" ||
    exitState === "error" ||
    (allControllerRowsUnknown && allExitRowsUnknown);
  const dumpUnknown = dumpCapacity?.riskBand === "unknown" || dumpState === "error" ? 1 : 0;
  const identityUnknown = identityReport?.status === "unknown" ? 1 : 0;
  const unknownPieceCount = controllerGaps.length + unknownRows.length + dumpUnknown + identityUnknown;

  return {
    label: "COULDN'T VERIFY",
    headline: broadFailure
      ? "The data ghosted us. Don’t guess."
      : unknownPieceCount === 1
        ? "One important receipt is missing. No pretending."
        : "Some important receipts are missing. No pretending.",
    summary: broadFailure
      ? "A data-source problem, unsupported behavior, or missing context stays Unknown—not green."
      : controllerGaps.length && !unknownRows.length
        ? notAssessedFindings.length && !unknownFindings.length
          ? "Current exit evidence may look healthy, but a declared pool-decoder boundary prevents a reassuring call."
          : "Current exit evidence may look healthy, but an unresolved controller area prevents a reassuring call."
        : !controllerGaps.length && unknownRows.length
          ? "Controller checks may look clear, but one or more exit sizes could not be verified."
          : "The completed checks are useful, but the remaining gaps prevent a reassuring call.",
    tone: "unknown",
    checks,
    reasons: pickReasons([
      identityState === "error"
        ? "The identity-registry check failed before it could reach a conclusion."
        : identityUnknown
          ? "One or more identity registry checks did not complete."
          : null,
      capabilityState === "error" ? "The controller-capability check failed before it could reach a conclusion." : null,
      dumpState === "error" ? "The holder-concentration check failed before it could reach a conclusion." : null,
      exitState === "error" ? "The exit-liquidity check failed before it could reach a conclusion." : null,
      dumpUnknown ? "Large-holder ownership could not be classified well enough for a dump-capacity conclusion." : null,
      controllerGaps.length ? controllerGapReason(controllerGaps) : null,
      unknownRows.length ? `${pluralize(unknownRows.length, "exit size")} could not be verified.` : null,
    ]),
    caveat,
  };
}

function buildIdentityCheck(
  report: IdentityReport | null,
  state: EvidenceSectionState,
): SimpleCoreCheck {
  const base = {
    number: "01" as const,
    title: "Identity",
    question: "Is this the token you meant to inspect?",
  };
  if (state === "loading" || state === "idle") return { ...base, result: "Checking exact-address provenance…", tone: "checking" };
  if (state === "error" || !report) return { ...base, result: "Could not verify identity", tone: "unknown" };
  if (report.status === "registry_recognized") {
    const providers = new Set(report.sources.map((source) => source.provider));
    const result = providers.has("jupiter") && providers.has("coingecko")
      ? "Jupiter + CoinGecko recognize this exact mint"
      : providers.has("fixture") && report.sources.length > 1
        ? "Two synthetic registries match this exact mint"
        : providers.has("jupiter")
          ? "Jupiter verified this exact mint"
          : providers.has("coingecko")
            ? "CoinGecko lists this exact mint"
            : "A registry recognizes this exact mint";
    return { ...base, result, tone: "calm" };
  }
  if (report.status === "unknown") return { ...base, result: "Identity sources incomplete", tone: "unknown" };
  return { ...base, result: "Identity not confirmed", tone: "unknown" };
}

function buildControllerCheck(
  findings: CapabilityFinding[],
  observed: CapabilityFinding[],
  unknown: CapabilityFinding[],
  notAssessed: CapabilityFinding[],
  state: EvidenceSectionState,
): SimpleCoreCheck {
  const base = {
    number: "02" as const,
    title: "Rug capacity",
    question: "Can someone change the rules or take value?",
  };
  if (observed.length) {
    const directSevere = observed.filter(
      (finding) =>
        severeImpact.has(finding.impactClass) &&
        ["wallet_shaped", "token_multisig", "fixture"].includes(finding.control.kind),
    ).length;
    const contextualSevere = observed.filter(
      (finding) =>
        severeImpact.has(finding.impactClass) &&
        ["program_controlled", "unresolved"].includes(finding.control.kind),
    ).length;
    return {
      ...base,
      result: directSevere
        ? `${pluralize(directSevere, "severe directly controlled power")} observed`
        : contextualSevere
          ? `${pluralize(contextualSevere, "powerful control")} need protocol context`
        : `${pluralize(observed.length, "controller power")} observed`,
      tone: directSevere ? "danger" : "warning",
    };
  }
  if (state === "loading" || state === "idle") return { ...base, result: "Checking controller powers…", tone: "checking" };
  if (state === "error") return { ...base, result: "Could not verify controller powers", tone: "unknown" };
  if (unknown.length === 1 && unknown[0]?.id === "liquidity_withdrawal") {
    return { ...base, result: "Liquidity control couldn't be verified", tone: "unknown" };
  }
  if (unknown.length) return { ...base, result: `${pluralize(unknown.length, "area")} unresolved`, tone: "unknown" };
  if (notAssessed.length === 1 && notAssessed[0]?.id === "liquidity_withdrawal") {
    const poolType = notAssessed[0].liquidityControl?.poolType;
    return {
      ...base,
      result: poolType
        ? `Liquidity control not assessed for ${poolType}`
        : "Liquidity control not assessed for this pool type",
      tone: "not_assessed",
    };
  }
  if (notAssessed.length) {
    return {
      ...base,
      result: `${pluralize(notAssessed.length, "area")} outside this version's decoder`,
      tone: "not_assessed",
    };
  }
  if (findings.length) return { ...base, result: "No dangerous power found in checked areas", tone: "calm" };
  return { ...base, result: "No controller evidence available", tone: "unknown" };
}

function controllerGapReason(gaps: CapabilityFinding[]): string {
  if (gaps.length === 1 && gaps[0]?.id === "liquidity_withdrawal") {
    return gaps[0].status === "not_assessed"
      ? gaps[0].summary
      : "Liquidity ownership or pool-control evidence could not be resolved completely.";
  }
  return `${pluralize(gaps.length, "controller area")} remain unresolved or outside this version's decoder.`;
}

function buildDumpCheck(
  report: DumpCapacityReport | null,
  state: EvidenceSectionState,
): SimpleCoreCheck {
  const base = {
    number: "03" as const,
    title: "Dump capacity",
    question: "Could a concentrated holder overwhelm buyers?",
  };
  if (state === "loading" || state === "idle") return { ...base, result: "Classifying the largest holders…", tone: "checking" };
  if (state === "error" || !report) return { ...base, result: "Could not verify holder concentration", tone: "unknown" };
  if (report.riskBand === "high") {
    return {
      ...base,
      result: report.largestWalletShapedSharePercentage === null
        ? "High concentration observed"
        : `Largest wallet-shaped holder: ${formatPercent(report.largestWalletShapedSharePercentage)}`,
      tone: "warning",
    };
  }
  if (report.riskBand === "elevated") {
    return {
      ...base,
      result: report.largestWalletShapedSharePercentage === null
        ? "Elevated concentration observed"
        : `Meaningful holder: ${formatPercent(report.largestWalletShapedSharePercentage)}`,
      tone: "warning",
    };
  }
  if (report.riskBand === "unknown") {
    return { ...base, result: "Large owners need deeper classification", tone: "unknown" };
  }
  return {
    ...base,
    result: report.largestWalletShapedSharePercentage === null
      ? "No large wallet-shaped holder observed"
      : `Largest wallet-shaped holder: ${formatPercent(report.largestWalletShapedSharePercentage)}`,
    tone: "calm",
  };
}

function buildExitCheck(
  rows: ExitLadderRow[],
  failed: ExitLadderRow[],
  unknown: ExitLadderRow[],
  state: EvidenceSectionState,
): SimpleCoreCheck {
  const base = {
    number: "04" as const,
    title: "Exit liquidity",
    question: "What happens when someone tries to sell?",
  };
  if (failed.length) return { ...base, result: exitFailureReason(mostImportantExitFailure(failed)), tone: "danger" };
  if (state === "loading" || state === "idle") return { ...base, result: "Checking each exit size…", tone: "checking" };
  if (state === "error") return { ...base, result: "Could not verify exits", tone: "unknown" };
  if (unknown.length) return { ...base, result: `${pluralize(unknown.length, "size")} unverified`, tone: "unknown" };
  if (rows.length) {
    const lowestRecovery = lowestComparableRecovery(rows);
    return {
      ...base,
      result:
        lowestRecovery === null
          ? `${rows.length}/${rows.length} sizes returned proceeds`
          : `${rows.length}/${rows.length} sizes returned proceeds · weakest ${lowestRecovery.toFixed(1)}%`,
      tone: "calm",
    };
  }
  return { ...base, result: "No comparable exit-size result", tone: "unknown" };
}

function formatPercent(value: number): string {
  return `${value.toLocaleString("en-US", { maximumFractionDigits: value < 1 ? 2 : 1 })}%`;
}

function mostImportantExitFailure(rows: ExitLadderRow[]): ExitLadderRow {
  return rankExitFailures(rows)[0];
}

function rankExitFailures(rows: ExitLadderRow[]): ExitLadderRow[] {
  const rank = ["no_supported_route", "insufficient_liquidity", "simulation_failed", "poor_proceeds"];
  return [...rows].sort((a, b) => rank.indexOf(a.state) - rank.indexOf(b.state));
}

function exitFailureReason(row: ExitLadderRow): string {
  const size = formatExitSize(row);
  if (row.state === "no_supported_route") return `${size} exit found no supported route`;
  if (row.state === "insufficient_liquidity") return `${size} exit found insufficient supported liquidity`;
  if (row.state === "simulation_failed") return `${size} exit simulation failed`;
  if (row.estimatedRecoveryPercentage !== null) {
    return `${size} exit returned only ${row.estimatedRecoveryPercentage.toFixed(1)}% of its reference value`;
  }
  return `${size} exit returned poor proceeds`;
}

function formatExitSize(row: ExitLadderRow): string {
  if (row.requestedUsd === null) return `${compactNumber(Number(row.inputTokenAmount))}-token`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(row.requestedUsd);
}

function lowestComparableRecovery(rows: ExitLadderRow[]): number | null {
  const comparable = rows
    .map((row) => row.estimatedRecoveryPercentage)
    .filter((value): value is number => value !== null);
  return comparable.length ? Math.min(...comparable) : null;
}

function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return "raw-amount";
  return new Intl.NumberFormat("en-US", {
    notation: value >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: value < 10 ? 4 : 2,
  }).format(value);
}

function pluralize(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function pickReasons(reasons: Array<string | null>): string[] {
  return [...new Set(reasons.filter((reason): reason is string => Boolean(reason)))].slice(0, 3);
}
